import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { formatAmount } from 'utils/helper';

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingBottom: 12,
    alignItems: 'center',
    borderBottom: '1px solid #F5F7F9',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#000',
  },
  greenDot: {
    color: '#89CA45',
  },
  printDate: {
    fontSize: 8,
    color: 'black',
  },
  companyDetails: {
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  companyName: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  companyInfo: {
    fontSize: 8,
    color: '#000',
    marginBottom: 4,
  },
  infoContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  infoSection: {
    width: '48%',
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#000',
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  label: {
    width: 60,
    fontSize: 8,
    color: '#000',
    fontWeight: 400,
  },
  value: {
    fontSize: 8,
    color: '#000',
    textAlign: 'right',
    fontWeight: 500,
  },
  table: {
    marginTop: 0,
  },
  tableHeader: {
    fontSize: 8,
    fontWeight: 'bold',
    flexDirection: 'row',
    backgroundColor: '#F5F7F9',
    padding: '8px',
    borderBottomWidth: 1,
    borderBottomColor: '#F5F5F5',
  },
  tableRow: {
    fontSize: 8,
    fontWeight: 400,
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#F5F5F5',
    padding: '8px 8px',
  },
  col1: { width: '20%' },
  //  col2: { width: "35%" },
  col3: { width: '25%' },
  col4: { width: '20%' },
  col5: { width: '13%' },
  col6: { width: '13%' },
  col7: { width: '9%' },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 24,
    right: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 8,
    color: '#000',
  },
  pageNumber: {
    fontSize: 8,
    color: '#000',
    textAlign: 'right',
  },
  totalAmount: {
    fontSize: 8,
    fontWeight: 600,
    color: '#000',
  },
  total: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#000',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#F5F5F5',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
});

const PDF = ({ invoiceDetails, transactions }) => (
  <Document>
    {/* Split transactions into chunks of 10 for pagination */}
    {chunk(transactions, 17)?.map((pageTransactions, pageIndex, allPages) => (
      <Page key={pageIndex} size='A4' style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>
            PostEx<Text style={styles.greenDot}>.</Text>
          </Text>
          <Text style={styles.printDate}>Printed at: {format(new Date(), 'MM/dd/yyyy, hh:mm a')}</Text>
        </View>

        <View style={styles.companyDetails}>
          <Text style={styles.companyName}>{invoiceDetails?.merchant_id?.businessName}</Text>
          <Text style={styles.companyInfo}>{invoiceDetails?.merchant_id?.contactNumber}</Text>
          <Text style={styles.companyInfo}>{invoiceDetails?.merchant_id?.email}</Text>
          <Text style={styles.companyInfo}>{invoiceDetails?.merchant_id?.website}</Text>
          <Text style={styles.companyInfo}>{invoiceDetails?.merchant_id?.address}</Text>
        </View>

        {pageIndex === 0 && (
          <>
            <View style={styles.infoContainer}>
              <View style={styles.infoSection}>
                <Text style={styles.sectionTitle}>Invoice Details</Text>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Invoice Number</Text>
                  <Text style={styles.value}>{invoiceDetails?._id}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Amount</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.invoice_gross_amount, 'PKR')}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Status</Text>
                  <Text style={styles.value}>{invoiceDetails?.status}</Text>
                </View>
                {invoiceDetails?.status === 'settled' && (
                  <View style={styles.detailRow}>
                    <Text style={styles.label}>Settlement Date</Text>
                    <Text style={styles.value}>
                      {formatInTimeZone(invoiceDetails?.settlement_date ? invoiceDetails?.settlement_date : invoiceDetails?.updatedAt, 'Asia/Karachi', 'MMMM d, yyyy, h:mm a')}
                    </Text>
                  </View>
                )}
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Created Date</Text>
                  <Text style={styles.value}>{formatInTimeZone(invoiceDetails?.createdAt, 'Asia/Karachi', 'MMMM d, yyyy, h:mm a')}</Text>
                </View>
              </View>

              <View style={styles.infoSection}>
                <Text style={styles.sectionTitle}>Invoice Summary</Text>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Transactions</Text>
                  <Text style={styles.value}>{invoiceDetails?.captured_transaction_count}</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.invoice_gross_amount, 'PKR')}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Charges</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.rate_charged_amount, 'PKR')}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Taxes</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.tax_charged_amount, 'PKR')}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Refund</Text>
                  <Text style={styles.value}>{invoiceDetails?.refunded_transaction_count}</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.refunded_amount, 'PKR')}</Text>
                </View>
                <View
                  style={[
                    styles.detailRow,
                    {
                      borderTopWidth: 1,
                      borderTopColor: '#F5F5F5',
                      paddingTop: 8,
                    },
                  ]}>
                  <Text style={[styles.label, styles.totalAmount]}>Net Amount</Text>
                  <Text style={[styles.value, styles.totalAmount]}>{formatAmount(invoiceDetails?.invoice_net_amount, 'PKR')}</Text>
                </View>
              </View>
            </View>
          </>
        )}

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.col1}>Order / Payment ID</Text>
            {/*  <Text style={styles.col2}>Payment Intent ID</Text> */}
            <Text style={styles.col3}>Time</Text>
            <Text style={styles.col4}>Status</Text>
            <Text style={[styles.col5, { textAlign: 'right' }]}>Amount</Text>
            <Text style={[styles.col6, { textAlign: 'right' }]}>Charges</Text>
            <Text style={[styles.col7, { textAlign: 'right' }]}>Tax</Text>
          </View>

          {pageTransactions?.map((transaction, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.col1}>{transaction.order_id}</Text>
              {/* <Text style={styles.col2}>{transaction.payment_intent_id}</Text> */}
              <Text style={styles.col3}>{formatInTimeZone(transaction?.createdAt, 'Asia/Karachi', 'MMMM d, yyyy, h:mm a')}</Text>
              <Text style={styles.col4}>{transaction?.status}</Text>
              <Text style={[styles.col5, { textAlign: 'right' }]}>{formatAmount(transaction?.amount, 'PKR')}</Text>
              <Text style={[styles.col6, { textAlign: 'right' }]}>{formatAmount(transaction?.invoiceStats?.feeChargeAmount, 'PKR')}</Text>
              <Text style={[styles.col7, { textAlign: 'right' }]}>{formatAmount(transaction?.invoiceStats?.regionTaxAmount, 'PKR')}</Text>
            </View>
          ))}

          {pageIndex === allPages?.length - 1 && (
            <View style={styles.total}>
              <Text style={styles.col1}></Text>
              <Text style={styles.col1}>Total</Text>
              <Text style={[styles.col5, { textAlign: 'right', paddingRight: 8 }]}>{formatAmount(invoiceDetails?.invoice_net_amount, 'PKR')}</Text>
            </View>
          )}
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>*Note: For any queries, please contact us at support@postexglobal.com</Text>
          <Text style={styles.pageNumber}>
            page {pageIndex + 1} of {allPages?.length}
          </Text>
        </View>
      </Page>
    ))}
  </Document>
);

// Add this utility function
const chunk = (arr, size) => Array?.from({ length: Math?.ceil(arr?.length / size) }, (v, i) => arr?.slice(i * size, i * size + size));

export default PDF;
